import type { Option } from '@bloomays-lib/ui.shared';
import type { ISociety } from '@bloomays-lib/types.shared';
import { IBAN, BIC } from 'ibankit';
import { startOfToday } from 'date-fns/startOfToday';
import { isAfter } from 'date-fns/isAfter';
import {
  EU_VAT_REGEX_STRING,
  FR_SIRET_REGEX_STRING,
  getSocietyStatusInfos,
  hasIbanSupport,
  addMonthToDate,
} from '@bloomays-lib/utils.shared';

import { deriveSocietyConstraints, type SocietyConstraints } from './missionDashboardV2';

/**
 * Règles métier PURES de la société fournisseur (Bloomer), extraites de `EditBloomerSociety` pour
 * être mono-sourcées entre le contrôle historique, le drawer du tableau de bord et un futur
 * formulaire V2. Aucune dépendance React / Apollo / i18n (les libellés, qui ont besoin de `i18n`,
 * restent côté présentation via `getSociety*Label`). S'appuie sur `getSocietyStatusInfos` et
 * `deriveSocietyConstraints` (déjà testés).
 */

export type SocietyFieldMode = 'siret' | 'european' | 'foreign';

/** Liste canonique des statuts juridiques fournisseur (libellés de la créa). */
export const supplierSocietyStatusOptions: Option[] = [
  { value: 'Auto Entrepreneur', label: '🇫🇷 Auto Entrepreneur', disabled: true },
  { value: 'EI', label: '🇫🇷 Entreprise Individuelle / Micro-entreprise (ex Auto Entrepreneur)' },
  { value: 'EURL', label: '🇫🇷 EURL' },
  { value: 'SARL', label: '🇫🇷 SARL' },
  { value: 'SAS', label: '🇫🇷 SAS' },
  { value: 'SASU', label: '🇫🇷 SASU' },
  { value: 'Autre', label: '🇫🇷 Autre forme Française' },
  { value: 'SUE', label: '🇪🇺 Societé Union Européenne' },
  { value: 'SHUE', label: '🌍 Societé Hors Union Européenne' },
];

/** En portage, on retire les formes micro/Auto-Entrepreneur (slice(2)), comme la créa. */
export const getSupplierStatusOptions = (portage?: boolean): Option[] =>
  portage ? supplierSocietyStatusOptions.slice(2) : supplierSocietyStatusOptions;

export type SupplierSocietyFormConfig = {
  constraints: SocietyConstraints;
  fieldMode: SocietyFieldMode;
  /** Numéro de TVA obligatoire (formes françaises hors micro, sociétés UE). */
  requiredVATNumber: boolean;
  /** Champ TVA affiché (masqué pour les sociétés hors-UE). */
  showVATTextField: boolean;
  isMicroEntrepreneur: boolean;
  isEuropeanSociety: boolean;
  isForeignSociety: boolean;
};

/** Configuration de formulaire (modes de champ + obligations TVA) dérivée du statut + portage. */
export const getSupplierSocietyFormConfig = (
  status: string | undefined,
  portage = false,
): SupplierSocietyFormConfig => {
  const { isEuropeanSociety, isForeignSociety, isMicroEntrepreneur } = getSocietyStatusInfos(status);
  const constraints = deriveSocietyConstraints(status, portage);

  let fieldMode: SocietyFieldMode = 'siret';
  let requiredVATNumber = false;
  let showVATTextField = true;
  switch (status) {
    case 'Auto Entrepreneur':
    case 'EI':
      requiredVATNumber = false;
      showVATTextField = true;
      fieldMode = 'siret';
      break;
    case 'SUE':
      // Société UE (TEC-3187) : le champ SIRET porte le numéro d'enregistrement local (format
      // libre, non exigé) ; seul le numéro de TVA intracommunautaire est obligatoire (regex TVA UE).
      requiredVATNumber = true;
      showVATTextField = true;
      fieldMode = 'european';
      break;
    case 'SHUE':
      requiredVATNumber = false;
      showVATTextField = false;
      fieldMode = 'foreign';
      break;
    default:
      requiredVATNumber = true;
      showVATTextField = true;
      fieldMode = 'siret';
      break;
  }

  return {
    constraints,
    fieldMode,
    requiredVATNumber,
    showVATTextField,
    isMicroEntrepreneur,
    isEuropeanSociety,
    isForeignSociety,
  };
};

/** Plafond (en mois) de la date d'expiration de chaque document. */
export const EXPIRATION_MAX_MONTHS = { kbis: 3, urssaf: 6, rcp: 12 } as const;

/** Type de document société soumis à une date d'expiration (KBIS / URSSAF / RCP). */
export type SocietyDocKind = keyof typeof EXPIRATION_MAX_MONTHS;

/**
 * Règle PARTAGÉE des dates d'expiration : on désactive toute date antérieure à aujourd'hui — une
 * attestation ne peut pas expirer dans le passé au moment de la saisie. Mono-sourcé entre le hook
 * `useBloomerSocietyForm`, `EditBloomerSociety` et le drawer Documents du tableau de bord.
 */
export const shouldDisableExpirationDate = (date: Date): boolean => isAfter(startOfToday(), date);

/**
 * Contraintes du sélecteur de date d'expiration par type de document : minimum = aujourd'hui,
 * maximum = fenêtre métier (`EXPIRATION_MAX_MONTHS`), dates passées désactivées. À étaler directement
 * sur le `DatePicker` (`{...getSocietyDocExpirationConstraints('kbis')}`) pour empêcher toute date fausse.
 */
export const getSocietyDocExpirationConstraints = (
  kind: SocietyDocKind,
  now: Date = new Date(),
): { minDate: Date; maxDate: Date; shouldDisableDate: (date: Date) => boolean } => ({
  minDate: now,
  maxDate: addMonthToDate(now, EXPIRATION_MAX_MONTHS[kind]),
  shouldDisableDate: shouldDisableExpirationDate,
});

export type ExpirationRequired = { kbis: boolean; urssaf: boolean; rcp: boolean };

/** Une date d'expiration est requise si le document est requis OU déjà présent. */
export const deriveExpirationRequired = (params: {
  kbisRequired: boolean;
  urssafRequired: boolean;
  hasKbis: boolean;
  hasUrssaf: boolean;
  hasRcp: boolean;
}): ExpirationRequired => ({
  kbis: params.kbisRequired || params.hasKbis,
  urssaf: params.urssafRequired || params.hasUrssaf,
  rcp: params.hasRcp,
});

/**
 * Normalise avant envoi : pays = FR si ni UE ni étrangère. Depuis TEC-3187, plus AUCUNE recopie
 * SIRET → TVA pour une société UE : le champ SIRET porte le numéro d'enregistrement local (format
 * libre) et le numéro de TVA intracommunautaire est saisi dans son propre champ (obligatoire).
 */
export const normalizeSupplierSocietyForSubmit = (society: ISociety): ISociety => {
  const { isEuropeanSociety, isForeignSociety } = getSocietyStatusInfos(society.status);
  const next: ISociety = { ...society };
  if (!isForeignSociety && !isEuropeanSociety) {
    next.addressCountryCode = 'FR';
  }
  return next;
};

/**
 * Les `*_REGEX_STRING` servent d'attribut HTML `pattern` (donc ancré ^…$ par le navigateur). Pour un
 * `RegExp.test()` équivalent on ancre explicitement la regex TVA UE (le SIRET FR l'est déjà).
 */
const euVatAnchored = new RegExp(`^(?:${EU_VAT_REGEX_STRING})$`);

/**
 * Regex d'immatriculation selon le mode de champ (mêmes patterns que
 * `SocietyRegistrationNumberTextField`). En mode européen le numéro d'enregistrement local est
 * libre (formats non maîtrisés, TEC-3187) — la TVA intracommunautaire a son propre champ.
 */
const registrationRegex = (fieldMode: SocietyFieldMode): RegExp => {
  if (fieldMode === 'european' || fieldMode === 'foreign') return /(\d||\w)+/;
  return new RegExp(FR_SIRET_REGEX_STRING);
};

export type SupplierSocietyFormatErrors = {
  siret?: string;
  tva?: string;
  iban?: string;
  bic?: string;
};

/**
 * Validation de FORMAT (et non de complétude) des champs renseignés, mono-sourcée avec les molécules
 * legacy (mêmes regex SIRET / TVA via `*_REGEX_STRING`, mêmes contrôles IBAN / BIC via `ibankit`).
 * Un champ vide n'est jamais en erreur : le tableau de bord autorise l'enregistrement partiel — on ne
 * bloque que sur une valeur mal formée. Le libellé SIRET suit le `fieldMode` (SIRET / TVA UE / étranger).
 */
export const validateSupplierSocietyFormat = (
  society: Partial<Pick<ISociety, 'siret' | 'tva' | 'IBAN' | 'BIC' | 'status' | 'addressCountryCode' | 'portage'>>,
  options?: { fieldMode?: SocietyFieldMode; supportIban?: boolean },
): SupplierSocietyFormatErrors => {
  const fieldMode = options?.fieldMode ?? getSupplierSocietyFormConfig(society.status, society.portage).fieldMode;
  const supportIban = options?.supportIban ?? hasIbanSupport(society.status, society.addressCountryCode || undefined);
  const errors: SupplierSocietyFormatErrors = {};

  const registrationLabel =
    fieldMode === 'european' ? "Numéro d'enregistrement" : fieldMode === 'foreign' ? "Numéro d'entreprise" : 'SIRET';
  if (society.siret && !registrationRegex(fieldMode).test(society.siret)) {
    errors.siret = `${registrationLabel} mal formé`;
  }
  if (society.tva && !euVatAnchored.test(society.tva)) {
    errors.tva = 'Numéro de TVA mal formé';
  }
  if (supportIban && society.IBAN && !IBAN.isValid(society.IBAN)) {
    errors.iban = 'IBAN mal formé';
  }
  if (society.BIC && !BIC.isValid(society.BIC)) {
    errors.bic = supportIban ? 'BIC mal formé' : 'SWIFT / BIC mal formé';
  }
  return errors;
};

export type SupplierSocietyMissingField =
  | 'name'
  | 'status'
  | 'siret'
  | 'country'
  | 'tva'
  | 'iban'
  | 'bic'
  | 'kbis'
  | 'urssaf';

/**
 * Champs/documents recommandés mais encore vides au regard des règles métier (statut + portage).
 * Sert au tableau de bord pour montrer VISUELLEMENT ce qui peut manquer — sans bloquer la sauvegarde
 * (complétude souple). Réutilise `getSupplierSocietyFormConfig` (donc `deriveSocietyConstraints`).
 */
export const deriveSupplierSocietyMissingFields = (
  society: Partial<
    Pick<
      ISociety,
      | 'name'
      | 'status'
      | 'siret'
      | 'tva'
      | 'IBAN'
      | 'BIC'
      | 'addressCountryCode'
      | 'portage'
      | 'KBIS'
      | 'URSSAFVigilanceCertificate'
    >
  >,
  extra?: { hasKbis?: boolean; hasUrssaf?: boolean },
): SupplierSocietyMissingField[] => {
  const { constraints, requiredVATNumber, showVATTextField, fieldMode } = getSupplierSocietyFormConfig(
    society.status,
    society.portage,
  );
  const missing: SupplierSocietyMissingField[] = [];
  if (!society.name) missing.push('name');
  if (!society.status) missing.push('status');
  // Société UE : le numéro d'enregistrement local est laissé libre, seule la TVA est exigée (TEC-3187).
  if (!society.siret && fieldMode !== 'european') missing.push('siret');
  if (constraints.countryRequired && !society.addressCountryCode) missing.push('country');
  if (showVATTextField && requiredVATNumber && !society.tva) missing.push('tva');
  if (constraints.ibanRequired && !society.IBAN) missing.push('iban');
  if (constraints.ibanRequired && !society.BIC) missing.push('bic');
  const hasKbis = extra?.hasKbis ?? Boolean(society.KBIS);
  const hasUrssaf = extra?.hasUrssaf ?? Boolean(society.URSSAFVigilanceCertificate);
  if (constraints.kbisRequired && !hasKbis) missing.push('kbis');
  if (constraints.urssafRequired && !hasUrssaf) missing.push('urssaf');
  return missing;
};

/** Variables `UPSERT_BLOOMER_SOCIETY` — payload unique partagé entre la créa et le drawer. */
export const buildBloomerSocietyUpsertInput = (society: ISociety, missionRecordId?: string) => ({
  name: society.name,
  recordId: society.recordId,
  status: society.status,
  siret: society.siret,
  KBIS: society.KBIS,
  KbisExpirationDate: society.KbisExpirationDate,
  URSSAFVigilanceCertificate: society.URSSAFVigilanceCertificate,
  URSSAFVCExpirationDate: society.URSSAFVCExpirationDate,
  RCPVigilanceCertificate: society.RCPVigilanceCertificate,
  RCPExpirationDate: society.RCPExpirationDate,
  IBAN: society.IBAN,
  BIC: society.BIC,
  portage: society.portage,
  bloomerBillingMission: missionRecordId ? [missionRecordId] : undefined,
  tva: society.tva,
  addressCountryCode: society.addressCountryCode,
});
